<template>
  <SettingsPageLayout>
    <div class="flex items-center justify-between w-11/12 max-w-3xl mx-auto">
      <h1 class="block text-2xl font-bold leading-6 text-gray-900">Custom Fields</h1>
      <TButton color="indigo" size="sm" label="Add field" @click="showNewModal = true" />
    </div>

    <div class="grid grid-cols-1 gap-1 w-11/12 max-w-3xl mx-auto mt-4">
      <div
        v-for="field in customFields"
        :key="`cf.${field.id}`"
        class="border border-gray-200 rounded p-6 flex items-center justify-between"
      >
        <div class="flex-grow">
          <header class="font-bold text-gray-900">{{ field.name }}</header>
          <aside class="flex flex-row gap-1">
            <span v-for="scope in field.scopes" :key="`cf.${field.id}.scopes.${scope}`" class="badge badge-primary">{{
              scope.toLowerCase().replace('contact:', '')
            }}</span>
          </aside>
        </div>
        <router-link :to="`/settings/data/custom_fields/${field.id}/edit`"
          ><TButton color="white" label="Edit"
        /></router-link>
      </div>

      <ZeroState v-if="!customFields || customFields.length === 0">
        <p>Your team does not have any custom fields created yet.</p>
      </ZeroState>
    </div>
    <Modal
      v-if="showNewModal"
      ref="modal"
      title="New Custom Field"
      body="Configure your custom field field here"
      action="Create field"
      action-color="indigo"
      @close-modal="showNewModal = false"
      @handle-action="createCustomField"
    >
      <FormKit type="form" :actions="false">
        <FormKit v-model="newCustomField.name" type="text" label="Field name" />
        <FormKit
          type="text"
          label="Field key"
          :model-value="newCustomFieldKey"
          :readonly="true"
          :disabled="true"
          style="background-color: #f0f0f0"
          help="This is generated automatically and you will use it when referencing this custom field in templates, documents, or other places. You cannot change it because it will break any existing references to this field."
        />
        <FormKit
          v-model="newCustomField.sensitive"
          type="checkbox"
          label="Sensitive"
          help="If this field contains sensitive information (ex. user logins, passwords, or tax information), you should delete this field and create a new one with the sensitive flag enabled."
        />
        <FormKit
          v-model="newCustomField.scopes"
          type="checkbox"
          :options="fieldScopes"
          label="Entity Type"
          :help="
            showPipelineReviewWarning
              ? 'Adding a custom field to Pipeline Reviews will remove our standard three questions that are asked during a Pipeline Review.'
              : null
          "
        />
        <FormKit
          v-if="showAutoCollect"
          v-model="newCustomField.auto_collect_during_onboarding"
          type="checkbox"
          label="Auto collect during teammate onboarding"
          help="When new agents go through the Humaniz onboarding process, do you want us to automatically get this from them?"
        />
        <FormKit v-model="newCustomField.field_type" type="select" label="Input type" :options="fieldTypeOptions" />
        <FormKit
          v-if="newCustomField.field_type === 'single_option' || newCustomField.field_type === 'multiple_options'"
          v-model="newCustomField.options"
          type="textarea"
          label="Options"
          rows="4"
        />
      </FormKit>
    </Modal>
  </SettingsPageLayout>
</template>

<script>
import { Api } from '../../../api/index.ts';
import Card from '../../Card/index.vue';
import SettingsPageLayout from '../../SettingsPageLayout/index.vue';
import DetailForm from '../../shared/DetailForm.vue';
import Modal from '../../shared/Modal.vue';
import ZeroState from '../../ZeroState.vue';

const BLANK_CUSTOM_FIELD = {
  field_type: 'short_text',
  name: '',
  scopes: ['contact:teammate', 'contact:candidate'],
  options: '',
  sensitive: false,
  auto_collect_during_onboarding: false,
};

export default {
  components: {
    Card,
    SettingsPageLayout,
    DetailForm,
    Modal,
    ZeroState,
  },
  data() {
    return {
      customFields: [],
      showNewModal: false,
      newCustomField: { ...BLANK_CUSTOM_FIELD },
    };
  },
  computed: {
    newCustomFieldKey() {
      return this.newCustomField.name.toLowerCase().replace(/\s/g, '_');
    },
    showAutoCollect() {
      return this.newCustomField.scopes.includes('contact:teammate');
    },
    showPipelineReviewWarning() {
      return this.newCustomField.scopes.includes('pipeline_review');
    },
    fieldTypeOptions() {
      return [
        {
          value: 'short_text',
          label: 'Text',
        },
        {
          value: 'single_option',
          label: 'Single option',
        },
        {
          value: 'multiple_options',
          label: 'Multiple options',
        },
        {
          value: 'date',
          label: 'Date',
        },
        {
          value: 'number',
          label: 'Number',
        },
        {
          value: 'percentage',
          label: 'Percentage',
        },
        {
          value: 'currency',
          label: 'Currency',
        },
        {
          value: 'rich_text',
          label: 'Rich text',
        },
        {
          value: 'address',
          label: 'Address',
        },
      ];
    },
    fieldScopes() {
      return { 'contact:candidate': 'Candidate', 'contact:teammate': 'Teammate', pipeline_review: 'Pipeline Review' };
    },
  },
  watch: {
    newCustomField: {
      handler(newValue, oldValue) {
        if (!newValue.scopes.includes('contact:teammate')) {
          newValue.auto_collect_during_onboarding = false;
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.fetchCustomFields();
    this.$store.commit('general/hideLocalLoadingSpinner');
  },
  methods: {
    async fetchCustomFields() {
      const { records } = await Api.CustomFields.listCustomFields();
      this.customFields = records;
      this.$store.commit('general/hideLocalLoadingSpinner');
    },
    createCustomField() {
      const body = {
        ...this.newCustomField,
        key: this.newCustomFieldKey,
        options: this.newCustomField.options.split(/\n/g),
      };
      Api.CustomFields.createCustomField({ body }).then(() => {
        this.showNewModal = false;
        this.newCustomField = { ...BLANK_CUSTOM_FIELD };
        this.fetchCustomFields();
      });
    },
  },
};
</script>

<style></style>
